import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { Spinner } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import {
  createStockItem,
  deleteStockItem,
  updateProductItemStock,
  updateStockItem,
} from '../../clients/product_client';
import {
  checkItemsForUseStatus,
  formatDateStr,
  getCurrentDate,
  isExpiryInSixMonths,
  isKitProduct,
  showToast,
  updateProductStockInProductList,
  updateStockItemSubStock,
} from '../../config_and_helpers/helpers';
import OrderDetailModal from '../Order/OrderDetailModal';
import DropdownSelect from '../Generic/DropdownSelect';
import './ProductComponentStyle.css';
import InventoryProgressBar from '../Generic/InventoryProgressBar';
import { PlusCircle, DashCircle } from 'react-bootstrap-icons';
import CustomProductContextHook from '../../pages/Product/CustomProductContextHook';
import { resolveStockItemOpeningChange } from '../../utils/productOpeningStatistics';
import { stockItemWithServerOpenFields } from '../../utils/stockItemOpenFields';
import {
  parseStoragePlace,
  readStockItemPlace,
  stockItemFieldsForPlace,
} from '../../utils/storagePlace';
import StoragePlaceSelect from './StoragePlaceSelect';
import {
  findUserOption,
  withOptionalNoUser,
} from '../../config_and_helpers/userDropdownOptions';
import {
  STOCK_ITEM_DATEPICKER_OVERFLOW_PROPS,
  logStockItemDatePickerOpen,
} from './stockItemDatePicker';
import {
  findOpenedByUserOption,
  logStockItemAssociatedUserChange,
  logStockItemOpenedByChange,
  resolveStockItemAssociatedUserId,
  stockItemOpenedByViewLabel,
  stockItemUserViewLabel,
} from './stockItemAssociatedUser';
import { logError, logInfo } from '../../config_and_helpers/logger';
import { refreshParentKitsInProductList } from '../../utils/kitStockLiveRefresh';

// checkmarkIcon: The icon for item save
const checkmarkIcon = (
  <i className="fa fa-check-circle" style={{ color: 'green' }}></i>
);

// cancelIcon: The icon for cancel item editing
const invalidIcon = (
  <i className="fa fa-solid fa-ban" style={{ color: 'red' }}></i>
);

// trashIcon: The icon for indicating deletion
const trashIcon = (
  <i className="fa fa-solid fa-trash" style={{ color: 'red' }}></i>
);

// editIcon: The icon for indicating editing
const editIcon = (
  <i className="fa fa-regular fa-pencil" style={{ color: 'green' }}></i>
);

/**
 * Cell presentation for "in use" highlight — frosted class in glass tables, legacy yellow inline otherwise.
 *
 * @param {boolean} useGlassTable
 * @param {boolean} inUse
 * @param {string} [extraClass]
 * @returns {{ className?: string, style?: Object }}
 */
function stockItemInUseCellProps(useGlassTable, inUse, extraClass = '') {
  const className = [extraClass, useGlassTable && inUse ? 'stock-items-table__in-use-cell' : '']
    .filter(Boolean)
    .join(' ');
  return {
    ...(className ? { className } : {}),
    ...(!useGlassTable && inUse ? { style: { backgroundColor: '#fafa98' } } : {}),
  };
}

/**
 * Row presentation for "in use" stock items.
 *
 * @param {boolean} useGlassTable
 * @param {boolean} inUse
 * @returns {{ className: string, style: Object|null }}
 */
function stockItemInUseRowProps(useGlassTable, inUse) {
  return {
    className: `text-center align-middle${
      useGlassTable && inUse ? ' stock-items-table__in-use-row' : ''
    }`,
    style: !useGlassTable && inUse ? { backgroundColor: '#ecec8c' } : null,
  };
}

const LOG_CONTEXT = 'StockItemComponent';

const PARENT_KIT_REFRESH_WARNING =
  'Kit complete-kit count could not be refreshed. Reopen the kit to see updated lots.';

/**
 * After a component lot create/delete, re-read kit rows already in the list.
 * Does not roll back the component mutation on failure.
 */
function refreshParentKitsAfterComponentLotChange({
  token,
  products,
  setProducts,
  componentId,
  product,
}) {
  if (isKitProduct(product)) return;
  if (!token || !Array.isArray(products) || products.length === 0) return;
  refreshParentKitsInProductList({
    token,
    products,
    componentId,
  })
    .then(({ products: withKits, failedIds }) => {
      setProducts(withKits);
      if (failedIds.length) {
        logError(LOG_CONTEXT, 'parent kit refresh failed', {
          componentId,
          failedIds,
        });
        showToast(PARENT_KIT_REFRESH_WARNING, 'warning', 'top-right', 4000);
      }
    })
    .catch(err => {
      logError(LOG_CONTEXT, 'parent kit refresh error', {
        componentId,
        message: err?.message ?? null,
      });
    });
}

/**
 * Represents a component for managing stock items.
 * User (associated) and Opened By are independent pickers. Opened By is enabled
 * in edit mode only when In Use is checked (same gate as Opened On).
 *
 * @param {object} StockItemComponent - The configuration object for the component.
 * @param {string} StockItemComponent.productId - The ID of the product.
 * @param {object} StockItemComponent.itemObj - The object representing the stock item.
 * @param {number} StockItemComponent.index - The index of the stock item.
 * @param {boolean} [StockItemComponent.editItem=false] - Indicates whether the stock item is being edited.
 * @param {function} StockItemComponent.showAddNewItem - A function for showing the add new item form.
 * @param {function} StockItemComponent.onSuccessfulSubmit - A function to be called after successful submission.
 * @param {number} StockItemComponent.unitQuantity - The unit quantity of the stock item.
 * @param {function} StockItemComponent.onStockUpdate - A function to be called after updating the stock.
 * @param {boolean} StockItemComponent.showBar - Indicates whether to show a bar in the component.
 * @param {function} [StockItemComponent.onOpeningHistoryChange] - Refetch product detail when opening history may have changed.
 * @param {Array|null} [StockItemComponent.storageNodes] - Lab storage tree. When set, the row can place the lot on any node.
 * @param {Array<{ value: string, label: string }>|null} [StockItemComponent.storagePlaceOptions] - Shared place options for all rows.
 * @param {{ box: Object, slotIndex: number|null, slotRequired?: boolean, onPlaced?: Function }|null} [StockItemComponent.placeHere]
 */
const StockItemComponent = ({
  productId,
  itemObj,
  index,
  editItem = false,
  showAddNewItem,
  onSuccessfulSubmit,
  unitQuantity,
  onStockUpdate,
  showBar,
  updateProductLogs,
  product, // Add product prop for kit functionality
  userSelectList = [],
  isLoadingUsers = false,
  onOpeningHistoryChange,
  useGlassTable = false,
  storageNodes = null,
  storagePlaceOptions = null,
  placeHere = null,
}) => {
  // Use the context hook to access the token.
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();
  // Use the context hook to access the products array
  const { products, setProducts } = CustomProductContextHook();

  // Use the state hook to manage item data.
  // Initialize with the provided itemObject or an empty object if not provided.
  const initialPlace = readStockItemPlace(itemObj);
  const [itemData, setItemData] = useState({
    id: itemObj?.id || '',
    openedBy: itemObj?.opened_by_name || '',
    associatedUserId: resolveStockItemAssociatedUserId(itemObj),
    batch: itemObj?.batch || '',
    expiry: itemObj?.expiry || '',
    inUse: itemObj?.in_use || false,
    openedOn:
      itemObj?.opened_on ||
      (itemObj?.in_use && !editItem ? getCurrentDate() : ''),
    itemStock: itemObj?.item_sub_stock || '',
    storageNodeId: initialPlace.storageNodeId,
    slotIndex: initialPlace.slotIndex,
  });

  // Use state hook to manage the submitting state of the form.
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Use state hook to manage the editing state of the item.
  const [showEdit, setShowEdit] = useState(editItem);
  // useState to manage the presentation of the expiry date error message
  const [expiryDateError, setExpiryDateError] = useState(false);
  // useState to manage the presentation of the expiry date error message
  const [openedOnDateError, setOpenedOnDateError] = useState(false);
  /** Date and opener to restore if In Use is turned back on before save. */
  const openFieldsBeforeCloseRef = useRef(null);
  const openingHistorySnapshotRef = useRef(null);

  const userOptions = useMemo(
    () => withOptionalNoUser(userSelectList),
    [userSelectList]
  );

  const handleAssociatedUserChange = option => {
    const isCleared =
      option == null || option.value === '' || option.label === 'No user';
    const nextUserId = isCleared ? null : option.value;
    logStockItemAssociatedUserChange({
      stockItemId: itemObj?.id ?? null,
      hasOrder: Boolean(itemObj?.order),
      associatedUserId: nextUserId,
    });
    setItemData(prev => ({
      ...prev,
      associatedUserId: nextUserId,
    }));
  };

  const handleOpenedByChange = option => {
    const isCleared =
      option == null || option.value === '' || option.label === 'No user';
    const nextOpenedBy = isCleared ? '' : String(option.label || '').trim();
    logStockItemOpenedByChange({
      stockItemId: itemObj?.id ?? null,
      hasOrder: Boolean(itemObj?.order),
      openedBy: nextOpenedBy,
      associatedUserId: itemData.associatedUserId ?? null,
    });
    setItemData(prev => ({
      ...prev,
      openedBy: nextOpenedBy,
    }));
  };

  const renderAssociatedUserDropdown = () => {
    if (isLoadingUsers) {
      return <Spinner animation="border" size="sm" />;
    }
    return (
      <div className="stock-item-user-select">
        <DropdownSelect
          optionsList={userOptions}
          placeholder="Select user (optional)"
          selectedValue={findUserOption(userOptions, itemData.associatedUserId)}
          setSelectedValue={handleAssociatedUserChange}
          disabledOptions={[]}
          clearable
        />
      </div>
    );
  };

  const renderOpenedByDropdown = () => {
    if (isLoadingUsers) {
      return <Spinner animation="border" size="sm" />;
    }
    const openerSelectEnabled = Boolean(itemData.inUse);
    return (
      <div className="stock-item-user-select">
        <DropdownSelect
          optionsList={userOptions}
          placeholder={
            openerSelectEnabled
              ? 'Select opener (optional)'
              : 'Mark In Use to select opener'
          }
          selectedValue={findOpenedByUserOption(userOptions, itemData.openedBy)}
          setSelectedValue={handleOpenedByChange}
          disabledOptions={[]}
          disabled={!openerSelectEnabled}
          clearable
        />
      </div>
    );
  };

  // The error message for the date errors
  const dateYearError = 'Year value must be 4 digits long';

  // useEffect to update the item stock in case the unit value for the product is edited
  useEffect(() => {
    if (itemData.itemStock > unitQuantity) {
      setItemData(prevItemData => ({
        ...prevItemData,
        itemStock: Number(unitQuantity),
      }));
    }
  }, [unitQuantity, itemData.itemStock]);

  // useEffect to sync local state with prop changes (especially for in_use status updates)
  useEffect(() => {
    if (itemObj) {
      setItemData(prevItemData => ({
        ...prevItemData,
        id: itemObj.id || '',
        openedBy: itemObj.opened_by_name || '',
        associatedUserId: resolveStockItemAssociatedUserId(itemObj),
        batch: itemObj.batch || '',
        expiry: itemObj.expiry || '',
        inUse: itemObj.in_use || false,
        openedOn:
          itemObj.opened_on ||
          (itemObj.in_use && !editItem ? getCurrentDate() : ''),
        itemStock: itemObj.item_sub_stock || '',
        storageNodeId: readStockItemPlace(itemObj).storageNodeId,
        slotIndex: readStockItemPlace(itemObj).slotIndex,
      }));
    }
  }, [itemObj, editItem]);

  // Const containing the object returned by the isExpiryInSixMonths function
  const isExpiredObject = isExpiryInSixMonths(itemData?.expiry);

  // Function used to handle the inputChange from the stockItem form
  const handleInputChange = input => {
    // Destructure the name and value properties from the input object
    const { name, value } = input.target ? input.target : input;

    // Check if the name of the input is either "openedOn" or "expiry"
    if (name === 'openedOn' || name === 'expiry') {
      // Convert the input value to a date
      const date = new Date(value);

      // Extract the year from the date
      const year = date.getFullYear();

      // Check if the year extracted from the date has more than 4 characters or not a number
      if (year.toString().length > 4 || isNaN(year)) {
        // If the name of the input is "openedOn", set an error for the "openedOn" date
        if (name === 'openedOn') {
          setOpenedOnDateError(true);

          // If the name of the input is "expiry", set an error for the "expiry" date
        } else if (name === 'expiry') {
          setExpiryDateError(true);
        }
      } else {
        // If the year is valid and the name of the input is "openedOn", clear any "openedOn" date errors
        if (name === 'openedOn') {
          setOpenedOnDateError(false);

          // If the year is valid and the name of the input is "expiry", clear any "expiry" date errors
        } else if (name === 'expiry') {
          setExpiryDateError(false);
        }
      }
    }

    // Update the item data state with the new value from the input
    setItemData(prevData => ({
      ...prevData,
      [name]: value || '',
    }));
  };

  // Toggle the editing state.
  const showEditItem = () => {
    if (showEdit) {
      openingHistorySnapshotRef.current = {
        openedOn: itemData.openedOn || '',
        inUse: Boolean(itemData.inUse),
      };
    }
    setShowEdit(!showEdit);
  };

  // Reset the item state to the initial itemObject provided in props.
  const resetItem = () => {
    // if this component was rendered when creating a new item but was cancelled, remove that row
    if (showAddNewItem) showAddNewItem();

    const place = readStockItemPlace(itemObj);
    setItemData({
      batch: itemObj ? itemObj.batch : '',
      openedBy: itemObj ? itemObj.opened_by_name || '' : '',
      associatedUserId: resolveStockItemAssociatedUserId(itemObj),
      expiry: itemObj ? itemObj.expiry : '',
      inUse: itemObj ? itemObj.in_use : false,
      openedOn: itemObj ? itemObj.opened_on : '',
      itemStock: itemObj ? itemObj.item_sub_stock : '',
      storageNodeId: place.storageNodeId,
      slotIndex: place.slotIndex,
    });
  };

  /**
   * Patch this product's list row after a lot save.
   * Reads the latest list so a stale render cannot drop the opened-lot count.
   *
   * @param {object} listArgs - Args for `updateProductStockInProductList` besides products and productId
   * @param {{ refreshKits?: boolean }} [options]
   */
  const applyProductListStock = (listArgs, { refreshKits = false } = {}) => {
    setProducts(prev => {
      const list = Array.isArray(prev) ? prev : [];
      if (list.length === 0) return prev;
      const nextList = updateProductStockInProductList({
        products: list,
        productId,
        ...listArgs,
      });
      logInfo(LOG_CONTEXT, 'product list stock patched', {
        productId,
        previousInUse:
          listArgs.previousInUse === undefined ? null : Boolean(listArgs.previousInUse),
        nextInUse: listArgs.nextInUse === undefined ? null : Boolean(listArgs.nextInUse),
        refreshKits,
      });
      if (refreshKits) {
        refreshParentKitsAfterComponentLotChange({
          token,
          products: nextList,
          setProducts,
          componentId: productId,
          product,
        });
      }
      return nextList;
    });
  };

  // Handle form submission i.e updating or creating new item.
  const handleSubmit = () => {
    setIsSubmitting(true);
    logInfo(LOG_CONTEXT, 'submit stock item', {
      stockItemId: itemObj?.id ?? null,
      productId,
      hasOrder: Boolean(itemObj?.order),
      associatedUserId: itemData.associatedUserId ?? null,
      inUse: Boolean(itemData.inUse),
      isCreate: !itemObj,
      storageNodeId: itemData.storageNodeId ?? null,
      slotIndex: itemData.slotIndex ?? null,
    });

    let requestData = itemData;
    if (storageNodes) {
      const place = parseStoragePlace(
        storageNodes,
        itemData.storageNodeId,
        itemData.slotIndex
      );
      if (place.error) {
        showToast(place.error, 'warning', 'top-right', 3500);
        setIsSubmitting(false);
        return;
      }
      requestData = {
        ...itemData,
        storageNodeId: place.storage_node_id,
        slotIndex: place.slot_index,
      };
    } else {
      requestData = { ...itemData };
      delete requestData.storageNodeId;
      delete requestData.slotIndex;
    }

    // Set the promise const depending on if creating or editing a stock item
    const stockItemPromise = itemObj
      ? updateStockItem(token, itemObj.id, requestData)
      : createStockItem(token, productId, requestData, false, product);

    stockItemPromise.then(response => {
      if (response && response.success) {
        // Leaving In Use nulls opened_on and opened_by_name on the server
        // even when this request still sent the previous values.
        const savedItem = editItem
          ? stockItemWithServerOpenFields(response.data)
          : response.data;
        const serverOpenedOn = editItem ? savedItem?.opened_on ?? null : null;
        const serverOpenedBy = editItem ? savedItem?.opened_by_name ?? null : null;
        if (editItem) {
          logInfo(LOG_CONTEXT, 'stock item save applied server open fields', {
            stockItemId: savedItem?.id ?? itemObj?.id ?? null,
            inUse: Boolean(savedItem?.in_use),
            openedOn: serverOpenedOn,
            openedBy: serverOpenedBy,
          });
          setItemData(prev => ({
            ...prev,
            inUse: Boolean(savedItem?.in_use),
            openedOn: serverOpenedOn || '',
            openedBy: serverOpenedBy || '',
          }));
          openFieldsBeforeCloseRef.current = null;
        }
        setTimeout(() => {
          response.toast(); // Triggers a success toast message.
          setIsSubmitting(false); // Resets the submitting state.
          if (!editItem) {
            //Update the frontend state managing the stock items array to represent the change
            onSuccessfulSubmit({ data: response.data });
            // Create counts as previously closed. Bump Opened only when the new lot is in use.
            applyProductListStock(
              {
                operation: 'add',
                previousInUse: false,
                nextInUse: Boolean(itemData.inUse),
              },
              { refreshKits: true }
            );
          } else {
            // For editing, only call onSuccessfulSubmit directly to update local state.
            // Open date and opener are the server item, not the posted values.
            onSuccessfulSubmit({
              data: savedItem,
              replaceSingleItem: true,
            });
            const snapshot = openingHistorySnapshotRef.current;
            applyProductListStock({
              operation: null,
              updatedStockItemObj: savedItem,
              editingStockItem: editItem,
              previousInUse: Boolean(snapshot ? snapshot.inUse : itemObj?.in_use),
              nextInUse: Boolean(savedItem?.in_use),
            });
          }
          // Determine the data source and update the product logs accordingly
          const productLogData = editItem ? savedItem : response.data.item;
          checkItemsForUseStatus(productLogData, updateProductLogs);
          const reportedOpenedOn = editItem
            ? serverOpenedOn || ''
            : itemData.openedOn;
          const reportedInUse = editItem
            ? Boolean(savedItem?.in_use)
            : Boolean(itemData.inUse);
          const openingChange = resolveStockItemOpeningChange({
            snapshot: openingHistorySnapshotRef.current,
            openedOn: reportedOpenedOn,
            inUse: reportedInUse,
            isCreate: !editItem,
          });
          if (onOpeningHistoryChange && openingChange.changed) {
            // Report the direction so parents can branch: closing a lot removes
            // its opening record, and for a kit it also closes the component
            // lots server-side. Open date and opener are the server values.
            onOpeningHistoryChange({
              stockItemId: itemObj?.id ?? savedItem?.id ?? response.data?.id ?? null,
              productId,
              inUse: reportedInUse,
              openedOn: reportedOpenedOn || '',
              isCreate: !editItem,
              closed: openingChange.closed,
            });
          }
          openingHistorySnapshotRef.current = null;
          showEditItem(); // Moves out of the editing mode.
        }, 1000);
      } else {
        // Displays an error toast if the submission fails.
        showToast(
          'An unexpected error occurred. Please try again in a little while.',
          'error',
          'top-right',
          3000
        );
        setIsSubmitting(false); // Resets the submitting state.
      }
    });
  };

  // Handle the deletion of stock item.
  const handleDelete = () => {
    setIsSubmitting(true);

    // Call the deletion axios function
    deleteStockItem(token, itemObj.id, product, itemObj).then(response => {
      if (response && response.success) {
        setTimeout(() => {
          // Callback function on successful submission updating the frontend state managing the stock items.
          onSuccessfulSubmit({ data: itemObj, deleteItem: true });
          response.toast(); // Triggers a success toast message.
          // Deleting an in-use lot closes it. A closed lot does not change Opened.
          applyProductListStock(
            {
              operation: 'subtract',
              deletedStockItemObj: itemObj,
              previousInUse: Boolean(itemObj?.in_use),
              nextInUse: false,
            },
            { refreshKits: true }
          );
          setIsSubmitting(false); // Resets the submitting state.
        }, 1000);
      } else {
        // Displays an error toast if the submission fails.
        showToast(
          'An unexpected error occurred. Please try again in a little while.',
          'error',
          'top-right',
          3000
        );
        setIsSubmitting(false); // Resets the submitting state.
      }
    });
  };

  // Handle the updating of stock
  const handleUpdateStock = updatedValue => {
    setItemData(prevState => ({
      ...prevState,
      itemStock: updatedValue,
    }));
  };

  const handleSubmitUpdateStock = action => {
    setIsSubmitting(true);
    const updatedStock =
      action === 'add' ? itemData.itemStock + 1 : itemData.itemStock - 1;
    // Call the deletion axios function
    updateProductItemStock(token, itemObj.id, updatedStock).then(response => {
      if (response && response.success) {
        setTimeout(() => {
          handleUpdateStock(updatedStock);
          onStockUpdate(action);
          response.toast(); // Triggers a success toast message.
          setIsSubmitting(false); // Resets the submitting state.
          if (onSuccessfulSubmit)
            onSuccessfulSubmit({
              data: response.data,
              replaceSingleItem: true,
            });
          // If the item stock equals to 1 prior to the deletion process, delete the stock item
          if (itemData.itemStock === 1) {
            handleDelete();
          } else {
            //Update the products array to reflect this change on the frontend (only if products context is available)
            if (products && products.length > 0) {
              setProducts(
                updateStockItemSubStock(
                  products,
                  productId,
                  itemData.id,
                  action,
                  null,
                  response.data,
                  onStockUpdate
                )
              );
            }
          }
        }, 1000);
      } else {
        // Displays an error toast if the submission fails.
        showToast(
          'An unexpected error occurred. Please try again in a little while.',
          'error',
          'top-right',
          3000
        );
        setIsSubmitting(false); // Resets the submitting state.
      }
    });
  };

  /**
   * Place this lot into the shelf, drawer, rack, or box the storage map already
   * chose (and a slot when that destination is a slotted box). Sends the lot's
   * current fields plus the place, same as any other lot save.
   */
  const handlePlaceHere = async () => {
    if (!placeHere || !itemObj?.id) return;
    const place = parseStoragePlace(
      [placeHere.box],
      placeHere.box?.id,
      placeHere.slotIndex
    );
    if (place.error) {
      showToast(place.error, 'warning', 'top-right', 3500);
      return;
    }
    setIsSubmitting(true);
    logInfo(LOG_CONTEXT, 'place lot from inventory modal', {
      stockItemId: itemObj.id,
      boxId: place.storage_node_id,
      slotIndex: place.slot_index,
    });
    const response = await updateStockItem(token, itemObj.id, {
      ...stockItemFieldsForPlace(itemObj),
      storageNodeId: place.storage_node_id,
      slotIndex: place.slot_index,
    });
    if (response && response.success) {
      response.toast?.();
      logInfo(LOG_CONTEXT, 'lot placed from inventory modal', {
        stockItemId: itemObj.id,
        boxId: place.storage_node_id,
        slotIndex: place.slot_index,
        placementDropped: Boolean(response.placementDropped),
      });
      placeHere.onPlaced?.({
        boxId: place.storage_node_id,
        slotIndex: place.slot_index,
        fromId: readStockItemPlace(itemObj).storageNodeId,
        placed: !response.placementDropped,
      });
      return;
    }
    const message = Array.isArray(response)
      ? response.filter(Boolean).join(' ')
      : 'Could not place this lot';
    logError(LOG_CONTEXT, 'place lot from inventory modal failed', {
      stockItemId: itemObj.id,
      response,
    });
    showToast(message || 'Could not place this lot', 'warning', 'top-right', 3500);
    setIsSubmitting(false);
  };

  const placeHereDisabled =
    Boolean(placeHere?.slotRequired) &&
    (placeHere?.slotIndex == null || placeHere?.slotIndex === '');

  return (
    <>
      {/* We create a new table row for each stock item */}
      <tr {...stockItemInUseRowProps(useGlassTable, Boolean(itemObj?.in_use))}>
        {/* This table data shows the index of the stock item (we add 1 because indices start at 0 while we want to start counting from 1)*/}
        <td {...stockItemInUseCellProps(useGlassTable, itemData.inUse)}>
          {index + 1 || ''}
        </td>
        {/* Here we show the order id of the stock item if it exists */}
        <td {...stockItemInUseCellProps(useGlassTable, itemData.inUse)}>
          {itemObj?.order ? (
            <OrderDetailModal orderId={itemObj.order.id} />
          ) : (
            'N/A'
          )}
        </td>
        {/* Associated user — independent of Opened By */}
        <td
          {...stockItemInUseCellProps(
            useGlassTable,
            itemData.inUse,
            'stock-items-table__user-col',
          )}
        >
          {showEdit
            ? stockItemUserViewLabel({
                itemObj,
                userOptions,
                associatedUserId: itemData.associatedUserId,
              })
            : renderAssociatedUserDropdown()}
        </td>
        {/* Here we show the order reception dates of the stock item if it exists */}
        <td {...stockItemInUseCellProps(useGlassTable, itemData.inUse)}>
          {itemObj?.order
            ? itemObj.order.reception_dates &&
              itemObj.order.reception_dates.length > 0
              ? itemObj.order.reception_dates.map((date, index) => (
                  <div key={index}>
                    {date}
                    {index < itemObj.order.reception_dates.length - 1 && <br />}
                  </div>
                ))
              : 'N/A'
            : 'N/A'}
        </td>
        {/* Opened By — independent of associated User; gated by In Use */}
        <td
          {...stockItemInUseCellProps(
            useGlassTable,
            itemData.inUse,
            'stock-items-table__user-col',
          )}
        >
          {showEdit
            ? stockItemOpenedByViewLabel({
                openedBy: itemData.openedBy,
              })
            : renderOpenedByDropdown()}
        </td>
        <td {...stockItemInUseCellProps(useGlassTable, itemData.inUse)}>
          {/* This section is a conditional rendering. If showEdit is true, we display the batch number else we display the input to edit it*/}
          {showEdit ? (
            itemData.batch
          ) : (
            <Form.Control
              type="text"
              name="batch"
              value={itemData.batch}
              onChange={handleInputChange}
              style={{ textAlign: 'center' }}
            />
          )}
        </td>
        <td
          {...(itemData.expiry && isExpiredObject.expired
            ? useGlassTable
              ? {
                  className: 'stock-items-table__expired-cell',
                  style: { color: 'white' },
                }
              : { style: { backgroundColor: '#f84f4f', color: 'white' } }
            : stockItemInUseCellProps(useGlassTable, itemData.inUse))}
        >
          {/* Similar conditional rendering for expiry date of an item */}
          {showEdit ? (
            <>
              <div>{itemData.expiry ? formatDateStr(itemData.expiry) : ''}</div>
              {itemData.expiry && isExpiredObject.expired && (
                <div style={{ fontSize: '12px', marginTop: '4px' }}>
                  {isExpiredObject.timeTillExpiry}
                </div>
              )}
            </>
          ) : (
            <Form.Group>
              <DatePicker
                {...STOCK_ITEM_DATEPICKER_OVERFLOW_PROPS}
                selected={itemData.expiry ? new Date(itemData.expiry) : null}
                onCalendarOpen={() =>
                  logStockItemDatePickerOpen('expiry', {
                    stockItemId: itemObj?.id ?? null,
                  })
                }
                onChange={date => {
                  if (date) {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    const dateValue = `${year}-${month}-${day}`;

                    if (year.toString().length <= 4) {
                      handleInputChange({
                        target: { name: 'expiry', value: dateValue },
                      });
                    }
                  } else {
                    handleInputChange({
                      target: { name: 'expiry', value: '' },
                    });
                  }
                }}
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/yyyy"
                className={`form-control ${
                  expiryDateError ? 'is-invalid' : ''
                }`}
                style={{ textAlign: 'center', width: '100%' }}
                wrapperClassName="w-100"
                inputClassName="text-center"
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                todayButton="Today"
              />
              {expiryDateError && (
                <div className="invalid-feedback d-block">{dateYearError}</div>
              )}
            </Form.Group>
          )}
        </td>
        {/* Checkbox to indicate if the stock item is in use */}
        <td {...stockItemInUseCellProps(useGlassTable, itemData.inUse)}>
          <Form.Check
            className="bold-checkbox"
            type="checkbox"
            name="inUse"
            disabled={showEdit}
            checked={itemData.inUse}
            onChange={e => {
              const nextInUse = e.target.checked;
              logInfo(LOG_CONTEXT, 'in use toggled', {
                stockItemId: itemObj?.id ?? null,
                inUse: nextInUse,
                openerSelectEnabled: nextInUse,
                clearedOpenedOn: !nextInUse && Boolean(itemData.openedOn),
                clearedOpenedBy: !nextInUse && Boolean(itemData.openedBy),
              });
              if (!nextInUse) {
                // Show empty Opened on and Opened by immediately. Turning In
                // Use back on before save restores them; the server clears
                // both only when the save actually leaves In Use.
                openFieldsBeforeCloseRef.current = {
                  openedOn: itemData.openedOn || '',
                  openedBy: itemData.openedBy || '',
                };
                setItemData({
                  ...itemData,
                  inUse: false,
                  openedOn: '',
                  openedBy: '',
                });
                setOpenedOnDateError(false);
              } else {
                const restored = openFieldsBeforeCloseRef.current;
                openFieldsBeforeCloseRef.current = null;
                setItemData({
                  ...itemData,
                  inUse: true,
                  ...(restored
                    ? {
                        openedOn: restored.openedOn,
                        openedBy: restored.openedBy,
                      }
                    : {}),
                });
              }
            }}
          />
        </td>
        {/* Date indicating if and when the stock item was opened */}
        <td {...stockItemInUseCellProps(useGlassTable, itemData.inUse)}>
          {showEdit ? (
            <>
              <div>
                {itemData.openedOn ? formatDateStr(itemData.openedOn) : ''}
              </div>
            </>
          ) : (
            <Form.Group>
              <DatePicker
                {...STOCK_ITEM_DATEPICKER_OVERFLOW_PROPS}
                selected={
                  itemData.openedOn ? new Date(itemData.openedOn) : null
                }
                onCalendarOpen={() =>
                  logStockItemDatePickerOpen('openedOn', {
                    stockItemId: itemObj?.id ?? null,
                    inUse: itemData.inUse,
                  })
                }
                onChange={date => {
                  if (date) {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    const dateValue = `${year}-${month}-${day}`;

                    if (year.toString().length <= 4) {
                      handleInputChange({
                        target: { name: 'openedOn', value: dateValue },
                      });
                    }
                  } else {
                    handleInputChange({
                      target: { name: 'openedOn', value: '' },
                    });
                  }
                }}
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/yyyy"
                disabled={!itemData.inUse}
                className={`form-control ${
                  openedOnDateError ? 'is-invalid' : ''
                }`}
                style={{
                  textAlign: 'center',
                  width: '100%',
                  ...(useGlassTable
                    ? {}
                    : {
                        backgroundColor: !itemData.inUse ? '#f8f9fa' : 'white',
                      }),
                }}
                wrapperClassName="w-100"
                inputClassName="text-center"
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                todayButton="Today"
              />
              {openedOnDateError && (
                <div className="invalid-feedback d-block">{dateYearError}</div>
              )}
            </Form.Group>
          )}
        </td>
        {storageNodes ? (
          <td
            {...stockItemInUseCellProps(
              useGlassTable,
              itemData.inUse,
              'stock-items-table__place-col'
            )}
          >
            <StoragePlaceSelect
              nodes={storageNodes}
              options={storagePlaceOptions}
              storageNodeId={itemData.storageNodeId}
              slotIndex={itemData.slotIndex}
              readOnly={showEdit}
              idPrefix={`lot-${itemObj?.id ?? index}`}
              onChange={({ storageNodeId, slotIndex }) => {
                setItemData(prev => ({ ...prev, storageNodeId, slotIndex }));
              }}
            />
          </td>
        ) : null}
        {/* Multi-Component Column */}
        <td {...stockItemInUseCellProps(useGlassTable, itemData.inUse)}>
          {itemObj?.is_master_stock_item ? (
            <span className="badge bg-primary">Master Unit</span>
          ) : itemObj?.is_component_stock_item ? (
            <span className="badge bg-secondary">Sub Unit</span>
          ) : (
            <span className="text-muted">-</span>
          )}
        </td>
        {/* Condition rendering for showing buttons based on various states (submitting/edit mode etc.) */}
        <td {...stockItemInUseCellProps(useGlassTable, itemData.inUse)}>
          {isSubmitting ? (
            /* When the form is submitting, display a spinner */
            <Spinner
              size="sm"
              as="span"
              animation="border"
              role="status"
              aria-hidden="true"
            />
          ) : isViewer ? null : (
            /* If the user is in edit mode, show save and cancel buttons, if not, show edit and delete buttons */
            <>
              {!showEdit ? (
                <>
                  <Button
                    variant=""
                    size="sm"
                    onClick={() => {
                      handleSubmit();
                    }}
                    className="rounded-edge-button-right"
                    disabled={expiryDateError || openedOnDateError}
                  >
                    {checkmarkIcon}
                  </Button>
                  <Button
                    variant=""
                    size="sm"
                    onClick={() => {
                      showEditItem();
                      resetItem();
                      setExpiryDateError(false);
                      setOpenedOnDateError(false);
                    }}
                  >
                    {invalidIcon}
                  </Button>
                </>
              ) : (
                itemObj && (
                  <>
                    {placeHere ? (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-1"
                        disabled={placeHereDisabled}
                        onClick={handlePlaceHere}
                      >
                        Place here
                      </Button>
                    ) : null}
                    <Button
                      variant=""
                      size="sm"
                      onClick={showEditItem}
                      className="me-1"
                    >
                      {editIcon}
                    </Button>
                    <Button
                      variant=""
                      size="sm"
                      onClick={() => {
                        setIsSubmitting(true);
                        handleDelete();
                      }}
                    >
                      {trashIcon}
                    </Button>
                  </>
                )
              )}
            </>
          )}
        </td>
      </tr>
      {/* If the item unit is Box or Package, a bar and buttons will appear to display and control the stock inside
       that box or package */}
      {itemObj && itemData.inUse && showBar && !isViewer ? (
        <tr className={`text-center align-middle`}>
          <td colSpan={storageNodes ? 10 : 9}>
            <InventoryProgressBar
              currentValue={itemData.itemStock}
              totalValue={unitQuantity}
            />
          </td>
          <td>
            <Button
              variant=""
              size="lg"
              onClick={handleSubmitUpdateStock}
              className="me-1"
            >
              <DashCircle />
            </Button>
            <Button
              variant=""
              size="lg"
              onClick={() => handleSubmitUpdateStock('add')}
            >
              <PlusCircle />
            </Button>
          </td>
        </tr>
      ) : null}

    </>
  );
};
export default StockItemComponent;
